import ChannelLabel from '@/pages/opinion/components/ChannelLabel';
import useTabHistory from '@/pages/opinion/hooks/useTabHistory';
import { getOpinionFilterCond } from '@/pages/opinion/services';
import React, { useEffect, useMemo, useState } from 'react';
import { useGetRelativeDetails } from './useGetRelativeDetails';
import { GameInfoType } from '@op/operationLog/buriedPoint';
type TabType = 'opencritic-metacritic' | 'xbox' | 'playstation' | 'meta_store';
const iconStyle = { width: 16, height: 16 };
const textStyle = { marginRight: 4 };

export default function useGameStoreTabs({
  gameInfo,
}: {
  gameInfo: GameInfoType;
}) {
  const [hasXbox, setHasXbox] = useState(false);
  const [hasPlaystation, setHasPlaystation] = useState(false);
  const [hasMetaScore, setHasMetaScore] = useState(false);
  const [relative, setRelative] = useState({});
  const [relativePlatform, setRelativePlatform] = useState<Array<string>>([]);
  const [tabLoading, setTabLoading] = useState(false);

  const filterParams = useMemo(
    () => ({
      ...gameInfo,
      request_type: ['channel'],
    }),
    [gameInfo],
  );

  const { data: relativeDetails, loading } = useGetRelativeDetails({
    filter: filterParams,
  });

  useEffect(() => {
    setTabLoading(true);
    if (!loading) {
      getOpinionFilterCond({
        ...gameInfo,
        request_type: ['game_store_score_platforms'],
        un_request_type: ['topic_trans'],
      })
        .then((data: any) => {
          const { game_store_score_platforms } = data || {};
          setHasXbox(game_store_score_platforms?.includes?.('xbox') || false);
          setHasPlaystation(
            game_store_score_platforms?.includes?.('playstation') || false,
          );
          setHasMetaScore(
            game_store_score_platforms?.includes?.('meta_store') || false,
          );
          if (relativeDetails) {
            const platforms: string[] = [];
            const { edition_unified_id, entity_type, channels, id_type } =
              relativeDetails;

            setRelative({
              edition_unified_id,
              entity_type,
              id_type,
            });

            if (
              !game_store_score_platforms?.includes?.('xbox') &&
              channels?.includes('Xbox')
            ) {
              setHasXbox(true);
              platforms.push('xbox');
            }

            if (
              !game_store_score_platforms?.includes?.('playstation') &&
              channels?.includes('PS')
            ) {
              setHasPlaystation(true);
              platforms.push('playstation');
            }

            if (
              !game_store_score_platforms?.includes?.('meta_store') &&
              channels?.includes('meta')
            ) {
              setHasMetaScore(true);
              platforms.push('meta_store');
            }

            setRelativePlatform(platforms);
          }
        })
        .finally(() => {
          setTabLoading(false);
        });
    }
  }, [gameInfo, loading, relativeDetails]);

  const tabs = useMemo(() => {
    const tabs = [
      {
        id: 'opencritic-metacritic',
        label: (
          <>
            <ChannelLabel
              iconStyle={{ ...iconStyle, marginRight: 4 }}
              channel_name="metacritic"
              showText={false}
            ></ChannelLabel>
            <ChannelLabel
              iconStyle={iconStyle}
              textStyle={textStyle}
              channel_name="opencritic"
              replaceText={'Critic Sites'}
            ></ChannelLabel>
          </>
        ),
      },
    ];

    if (hasXbox) {
      tabs.push({
        id: 'xbox',
        label: (
          <>
            <ChannelLabel
              textStyle={textStyle}
              iconStyle={iconStyle}
              channel_name="xbox"
            ></ChannelLabel>
          </>
        ),
      });
    }

    if (hasPlaystation) {
      tabs.push({
        id: 'playstation',
        label: (
          <>
            <ChannelLabel
              textStyle={textStyle}
              iconStyle={iconStyle}
              channel_name="playstation"
            ></ChannelLabel>
          </>
        ),
      });
    }

    if (hasMetaScore) {
      tabs.push({
        id: 'meta_store',
        label: (
          <>
            <ChannelLabel
              textStyle={textStyle}
              iconStyle={iconStyle}
              channel_name="meta_store"
            ></ChannelLabel>
          </>
        ),
      });
    }

    return tabs;
  }, [hasXbox, hasPlaystation, hasMetaScore]);

  const { tab, onActive } = useTabHistory<TabType>({
    defaultValue: 'opencritic-metacritic',
  });

  return {
    tabs,
    tab,
    onActive,
    relative,
    relativePlatform,
    loading: loading || tabLoading,
  };
}
